import { nurVorraete } from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { poolsQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { Button } from './ui/primitives'
import { nebenBestandVerwerfen } from '../lib/mutationen'
import { hintergrundSchliesst } from './ui/hintergrund'

/**
 * Aufgabe in Pools einsortieren – **auch importierte** (F-17, feldweise Sperre).
 *
 * ## Warum das an einem GitLab-Issue erlaubt ist
 *
 * Pool-Zugehörigkeit gibt es im Quellsystem **nicht**. Ein GitLab-Issue in einen
 * Work-Pool zu legen ändert in GitLab nichts – es ist Steuerungsinformation, und
 * Steuerung ist der Zweck von Work (Konzept Kap. 1, Kap. 6.2). Gesperrt bleiben
 * nur die Felder, die der Importeur beim nächsten Abgleich überschreibt.
 *
 * ## Bis zum Drag & Drop
 *
 * AP 4.3 sieht Betanken per Drag & Drop vor. Diese Auswahl ist der Weg dorthin:
 * sie funktioniert mit der Tastatur, an jeder Liste und ohne Zeigegerät – und
 * bleibt danach als zweiter Weg bestehen (Leitplanke 3).
 */
export function PoolsSetzen({
  aufgabeId,
  titel,
  schliessen,
}: {
  aufgabeId: string
  titel: string
  schliessen: () => void
}) {
  /**
   * **Regale stehen hier nicht zur Wahl** (20.08.2026,
   * [E-191](../../../docs/ENTSCHEIDUNGEN.md)). Diese Auswahl setzt nur die Zugehörigkeit; der
   * Parkplatz braucht dazu den Status – deshalb geht er über „Parken" im Eingang, und der
   * Server weist ihn hier ab.
   */
  const { data: allePools = [] } = useQuery(poolsQuery)
  const pools = nurVorraete(allePools)
  /** Liegt die Aufgabe in einem Regal? Dann ist die Frage dieses Dialogs die falsche. */
  const geparktIn = allePools.filter(
    (p) => !p.ziehbar && p.aufgaben.some((a) => a.id === aufgabeId),
  )
  const queryClient = useQueryClient()
  const [fehler, setFehler] = useState<string | null>(null)

  /** Wo die Aufgabe schon liegt – aus den geladenen Pools abgeleitet. */
  const [gewaehlt, setGewaehlt] = useState<string[] | null>(null)
  const aktuell =
    gewaehlt ?? pools.filter((p) => p.aufgaben.some((a) => a.id === aufgabeId)).map((p) => p.id)

  const setzen = useMutation({
    mutationFn: (poolIds: string[]) => trpc.tasks.poolsSetzen.mutate({ id: aufgabeId, poolIds }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pools'] })
      void nebenBestandVerwerfen(queryClient)
      schliessen()
    },
    onError: (e: Error) => setFehler(e.message),
  })

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-50 flex items-start justify-center bg-[rgb(23_22_20/0.38)] p-3 pt-[4vh] sm:p-6 sm:pt-[14vh]"
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label="Pools setzen"
    >
      <div className="w-full min-w-0 max-w-[480px] rounded-card border border-line bg-surface p-[18px] shadow-card">
        <h3 className="text-sm font-bold">In welche Pools?</h3>
        <p className="mt-1 truncate text-[12.5px] text-ink2" title={titel}>
          {titel}
        </p>

        {/**
         * **Eine geparkte Aufgabe wird hier nicht einsortiert** (20.08.2026).
         *
         * Ohne diesen Hinweis stünden die Regale nicht in der Auswahl, ihre Zugehörigkeit
         * aber trotzdem in `aktuell` – und „Übernehmen" nähme die Aufgabe aus dem Parkplatz,
         * ohne den Status mitzunehmen. Das Ergebnis wäre eine zurückgestellte Aufgabe in
         * keinem Pool: unsichtbar in beiden Ansichten, in denen man sie suchen würde.
         */}
        {geparktIn.length > 0 && (
          <p className="mt-3 rounded-lg bg-[#fdf3dc] px-2.5 py-2 text-[12.5px] text-[#8a6100]">
            Diese Aufgabe liegt auf dem {geparktIn.map((p) => p.label).join(', ')} und ist
            zurückgestellt. Hol sie in den Pools zurück in den Eingang, wenn daraus Arbeit werden
            soll – von dort lässt sie sich wie jede andere einordnen.
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-1.5">
          {geparktIn.length === 0 &&
            pools.map((pool) => {
              const an = aktuell.includes(pool.id)
              return (
                <button
                  key={pool.id}
                  type="button"
                  onClick={() =>
                    setGewaehlt(an ? aktuell.filter((id) => id !== pool.id) : [...aktuell, pool.id])
                  }
                  aria-pressed={an}
                  className={`cursor-pointer rounded-lg px-[11px] py-[5px] text-xs font-semibold ${
                    an ? 'bg-ink text-white' : 'border border-line2 text-ink2 hover:bg-[#f3f2ef]'
                  }`}
                >
                  {pool.icon} {pool.label}
                </button>
              )
            })}
        </div>

        {geparktIn.length === 0 && (
          <p className="mt-2.5 text-[11px] text-muted">
            Eine Aufgabe darf in mehreren Pools liegen. Gezogen wird sie einmal und verschwindet
            dann aus allen. Keine Auswahl heißt: aus allen Pools nehmen.
          </p>
        )}

        {fehler && (
          <p className="mt-2.5 rounded-lg bg-[#fdeaea] px-2.5 py-2 text-[12.5px] text-[#b02a2a]">
            {fehler}
          </p>
        )}

        <div className="mt-3.5 flex gap-2">
          {geparktIn.length === 0 && (
            <Button
              variant="primary"
              size="sm"
              disabled={setzen.isPending}
              onClick={() => {
                setFehler(null)
                setzen.mutate(aktuell)
              }}
            >
              {setzen.isPending ? 'Speichert …' : 'Übernehmen'}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={schliessen}>
            {geparktIn.length > 0 ? 'Schließen' : 'Abbrechen'}
          </Button>
        </div>
      </div>
    </div>
  )
}
